/**
 * The single definition of how this application renders a date (LSEG-271).
 *
 * Every date shown to a user goes through here. The reason is a defect rather than
 * tidiness: roughly forty call sites called `toLocaleString()` with no locale
 * argument, which follows whatever the browser happens to be configured as, so a
 * US-configured machine rendered `8/28/2026` in a British product. `08/09` means two
 * different days depending on who reads it, so the ambiguity is a genuine misreading
 * risk rather than a cosmetic one.
 *
 * Two rules resolve that, and they are deliberately in tension.
 *
 * The reader's own culture is honoured, so a machine configured as German renders
 * `28.08.2026` rather than a British reading of the same instant. But a locale that
 * puts the month first is corrected to `en-GB`, because the day must never precede
 * the month anywhere in the product. `isDayFirstLocale` is what arbitrates, and it
 * asks Intl rather than consulting a hardcoded list, so it needs no maintenance as
 * locales come and go.
 *
 * ⚠️ **The culture read here is the browser's, not the interface's**, and the two
 * are structurally free to disagree. `src/i18n.ts` resolves the interface language
 * from `localStorage.preferredLanguage` and otherwise defaults to `en-GB`; it never
 * consults `navigator`, so a German-configured machine opens an English interface
 * and will read `28.08.2026` inside it. That is the deliberate reading of "format
 * according to browser settings" (LSEG-271) rather than an oversight, and the
 * day-first correction is what keeps the mismatch cosmetic. Note that a second
 * policy already exists: `formatRunTimestamp` in `src/pages/automations/utils/
 * runFormatting.ts` takes `i18n.language` from its call sites instead. It cannot
 * produce a month-first date, because every one of the six shipped interface
 * languages is day-first, so the two policies differ in which culture they follow
 * rather than in whether they are safe. Reconciling them is a decision to take
 * deliberately, not a tidy-up to perform in passing.
 *
 * Everything here is pure. The locale is an optional parameter resolved internally
 * when omitted, which is one public API rather than two: a call site passes nothing
 * and inherits the correct locale, so a view written next year cannot forget. Tests
 * pass a locale explicitly and never touch the browser global.
 */

/**
 * The product default, and the correction applied to any locale that would put the
 * month first. British rather than ISO because this is a British product and
 * `28/08/2026` is what a reader here expects.
 */
export const FALLBACK_LOCALE = "en-GB";

/** Anything a caller might be holding, before it is known to be a usable date. */
export type DateInput = Date | string | number | null | undefined;

type DateStyleKey = "dateTime" | "date" | "time" | "longDate";

/**
 * The four shapes this product renders, named once.
 *
 * The explicit `2-digit` day and month are load-bearing rather than decorative.
 * Without them `de-DE` renders `28.8.2026` and `es-ES` renders `28/8/2026`, each
 * carrying a single-digit month that fails a literal reading of DD/MM/YYYY.
 *
 * `month: "short"` is deliberately absent. It is not stable across the supported
 * set, since `pt-PT` collapses it back to a numeric month, so a short-month preset
 * would render inconsistently depending on who was reading.
 *
 * No `hour12` or `hourCycle` is set anywhere. Forcing one would hardcode a second
 * cultural assumption inside the change that removes the first, and a day-first
 * locale such as `en-AU` legitimately uses a twelve-hour clock.
 */
const DATE_STYLES: Readonly<Record<DateStyleKey, Intl.DateTimeFormatOptions>> = {
  /** `28/08/2026, 17:02` */
  dateTime: {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
  /** `28/08/2026` */
  date: { day: "2-digit", month: "2-digit", year: "numeric" },
  /** `17:02` */
  time: { hour: "2-digit", minute: "2-digit" },
  /** `28 August 2026`, for a document cover page where the long form cannot be misread. */
  longDate: { day: "numeric", month: "long", year: "numeric" },
};

/**
 * Constructing an `Intl.DateTimeFormat` is expensive, and `MessageCard` formats one
 * timestamp per message on every render, so the instances are cached. Keyed on the
 * locale and style together, since one locale is rendered in several shapes.
 *
 * ⚠️ **Neither cache evicts, which is safe only while the key space stays bounded.**
 * A key is a locale tag, and in practice a session sees one browser locale plus
 * whatever a caller passes explicitly, which today is nothing: `options.locale` on
 * the three template engines is the only public route in and no caller populates it.
 * A future caller feeding a per-document or user-supplied tag would turn these into
 * a session-lifetime leak, in an application that stays open all day. Bound the maps
 * before wiring up such a caller rather than after.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

/** Day-first is a fixed property of a locale, so it is worth resolving only once. */
const dayFirstCache = new Map<string, boolean>();

/**
 * True when the locale writes the day before the month.
 *
 * Asked of Intl through `formatToParts` rather than matched against a list of
 * locale tags, so it answers correctly for every locale including ones nobody
 * anticipated. A year-first locale such as `ja-JP` is false, which is the intended
 * reading: the question is whether the day comes first, not merely whether it
 * precedes the month.
 *
 * An unrecognised tag throws inside Intl, and is reported as not day-first so the
 * caller falls back rather than propagating the error into a render.
 */
export const isDayFirstLocale = (locale: string): boolean => {
  const cached = dayFirstCache.get(locale);

  if (cached !== undefined) return cached;

  let dayFirst: boolean;

  try {
    const parts = new Intl.DateTimeFormat(locale, DATE_STYLES.date).formatToParts(
      new Date(2026, 7, 28),
    );
    const first = parts.find(
      (part) => part.type === "day" || part.type === "month" || part.type === "year",
    );

    dayFirst = first?.type === "day";
  } catch {
    dayFirst = false;
  }

  dayFirstCache.set(locale, dayFirst);

  return dayFirst;
};

/**
 * The locale a date should actually be rendered in.
 *
 * Precedence is the explicit argument, then the browser and operating system
 * setting, then the product default. Whichever wins is then held to the day-first
 * rule, so the correction applies to an explicitly passed locale exactly as it does
 * to a detected one. That uniformity is the point: there is no route through this
 * module that can produce a month before a day.
 */
export const resolveLocale = (locale?: string): string => {
  const candidate =
    locale ||
    (typeof navigator !== "undefined" ? navigator.language : undefined) ||
    FALLBACK_LOCALE;

  return isDayFirstLocale(candidate) ? candidate : FALLBACK_LOCALE;
};

/** Narrows whatever the caller is holding to a usable date, or null. */
export const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === "") return null;

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatterFor = (locale: string, style: DateStyleKey): Intl.DateTimeFormat => {
  const key = `${locale}|${style}`;
  const cached = formatterCache.get(key);

  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat(locale, DATE_STYLES[style]);

  formatterCache.set(key, formatter);

  return formatter;
};

const format = (value: DateInput, style: DateStyleKey, locale?: string): string | null => {
  const date = toDate(value);

  return date ? formatterFor(resolveLocale(locale), style).format(date) : null;
};

/**
 * A date and time, for example `28/08/2026, 17:02`.
 *
 * Null for an absent or unparseable value, so the caller states what it does not
 * know. The alternative is the browser's literal `Invalid Date`, which is neither
 * translated nor meaningful, and which this product rendered to users until now.
 */
export const formatDateTime = (value: DateInput, locale?: string): string | null =>
  format(value, "dateTime", locale);

/** A date alone, for example `28/08/2026`. */
export const formatDate = (value: DateInput, locale?: string): string | null =>
  format(value, "date", locale);

/** A time alone, for example `17:02`. */
export const formatTime = (value: DateInput, locale?: string): string | null =>
  format(value, "time", locale);

/** A long-form date, for example `28 August 2026`. */
export const formatLongDate = (value: DateInput, locale?: string): string | null =>
  format(value, "longDate", locale);

/**
 * The time alone when the value falls on today's date, and a full date and time
 * otherwise. This is how a chat stamps its messages, where the date is noise for
 * the conversation happening now and necessary for one being scrolled back through.
 *
 * `now` is injected so both branches are testable without reaching for fake timers.
 */
export const formatSmartTimestamp = (
  value: DateInput,
  locale?: string,
  now: Date = new Date(),
): string | null => {
  const date = toDate(value);

  if (!date) return null;

  const isToday = date.toDateString() === now.toDateString();

  return format(date, isToday ? "time" : "dateTime", locale);
};
